import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { QueryProvider } from './providers/QueryProvider';
import { ProtectedRoute } from './components/auth';
import { ErrorBoundary, MainLayout, PageLoading, PanelLayout } from './components/layout';
import { usePageMetadata } from './hooks/usePageMetadata';
import { AnalyticsProvider } from './components/analytics/AnalyticsProvider';
import { CookieConsent } from './components/ui/CookieConsent';
import { usePageTracking } from './hooks/useAnalytics';
import { ADMIN_WORKSPACE_PERMISSIONS, PERMISSIONS } from './utils/permissions';

const LoginPage = lazy(() => import('./pages/auth/LoginPage').then((module) => ({ default: module.LoginPage })));
const InvitePage = lazy(() => import('./pages/auth/InvitePage').then((module) => ({ default: module.InvitePage })));
const PanelDashboardPage = lazy(() => import('./pages/panel/PanelDashboardPage').then((module) => ({ default: module.PanelDashboardPage })));
const PanelWorkspacePage = lazy(() => import('./pages/panel/PanelWorkspacePage').then((module) => ({ default: module.PanelWorkspacePage })));
const HomePage = lazy(() => import('./pages/home/HomePage').then((module) => ({ default: module.HomePage })));
const JobsPage = lazy(() => import('./pages/jobs/JobsPage').then((module) => ({ default: module.JobsPage })));
const JobDetailsPage = lazy(() => import('./pages/jobs/JobDetailsPage').then((module) => ({ default: module.JobDetailsPage })));
const AboutPage = lazy(() => import('./pages/static/AboutPage').then((module) => ({ default: module.AboutPage })));
const ContactPage = lazy(() => import('./pages/static/ContactPage').then((module) => ({ default: module.ContactPage })));
const ServicesPage = lazy(() => import('./pages/static/ServicesPage').then((module) => ({ default: module.ServicesPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/static/PrivacyPolicyPage').then((module) => ({ default: module.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/static/TermsOfServicePage').then((module) => ({ default: module.TermsOfServicePage })));

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const ResumePage = lazy(() => import('./pages/dashboard/ResumePage').then((module) => ({ default: module.ResumePage })));
const ProfilePage = lazy(() => import('./pages/dashboard/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const ApplicationsPage = lazy(() => import('./pages/dashboard/ApplicationsPage').then((module) => ({ default: module.ApplicationsPage })));
const MessagesPage = lazy(() => import('./pages/dashboard/MessagesPage').then((module) => ({ default: module.MessagesPage })));
const VideosPage = lazy(() => import('./pages/dashboard/VideosPage').then((module) => ({ default: module.VideosPage })));
const SavedJobsPage = lazy(() => import('./pages/dashboard/SavedJobsPage').then((module) => ({ default: module.SavedJobsPage })));
const TestimonialPage = lazy(() => import('./pages/dashboard/TestimonialPage').then((module) => ({ default: module.TestimonialPage })));

const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then((module) => ({ default: module.AdminDashboardPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage').then((module) => ({ default: module.AdminUsersPage })));
const AdminJobsPage = lazy(() => import('./pages/admin/AdminJobsPage').then((module) => ({ default: module.AdminJobsPage })));
const AdminJobCreatePage = lazy(() => import('./pages/admin/AdminJobCreatePage').then((module) => ({ default: module.AdminJobCreatePage })));
const AdminJobDetailView = lazy(() => import('./pages/admin/AdminJobDetailView').then((module) => ({ default: module.AdminJobDetailView })));
const AdminApplicationsPage = lazy(() => import('./pages/admin/AdminApplicationsPage').then((module) => ({ default: module.AdminApplicationsPage })));
const AdminResumesPage = lazy(() => import('./pages/admin/AdminResumesPage').then((module) => ({ default: module.AdminResumesPage })));
const AdminCompaniesPage = lazy(() => import('./pages/admin/AdminCompaniesPage').then((module) => ({ default: module.AdminCompaniesPage })));
const AdminCompanyDetailPage = lazy(() => import('./pages/admin/AdminCompanyDetailPage').then((module) => ({ default: module.AdminCompanyDetailPage })));
const AdminCandidatesPage = lazy(() => import('./pages/admin/AdminCandidatesPage').then((module) => ({ default: module.AdminCandidatesPage })));
const AdminCandidateProfilePage = lazy(() => import('./pages/admin/AdminCandidateProfilePage').then((module) => ({ default: module.AdminCandidateProfilePage })));
const AdminMessagesPage = lazy(() => import('./pages/admin/AdminMessagesPage').then((module) => ({ default: module.AdminMessagesPage })));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage').then((module) => ({ default: module.AdminAnalyticsPage })));
const AdminThemeSettingsPage = lazy(() => import('./pages/admin/AdminThemeSettingsPage').then((module) => ({ default: module.AdminThemeSettingsPage })));
const AdminSystemSettingsPage = lazy(() => import('./pages/admin/AdminSystemSettingsPage').then((module) => ({ default: module.AdminSystemSettingsPage })));
const AdminFileManagerPage = lazy(() => import('./pages/admin/AdminFileManagerPage').then((module) => ({ default: module.AdminFileManagerPage })));
const AdminTestimonialsPage = lazy(() => import('./pages/admin/AdminTestimonialsPage').then((module) => ({ default: module.AdminTestimonialsPage })));

function AdminJobApplicationsRedirect() {
  const { jobId } = useParams<{ jobId: string }>();
  return <Navigate to={`/admin/jobs/${jobId || ''}`} replace />;
}

function AppContent() {
  // Update page metadata (title and favicon) based on system settings
  usePageMetadata();
  
  // Track page views automatically
  usePageTracking();

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<MainLayout showBreadcrumbs={false}><HomePage /></MainLayout>} />
              <Route path="/jobs" element={<MainLayout><JobsPage /></MainLayout>} />
              <Route path="/jobs/:id" element={<MainLayout><JobDetailsPage /></MainLayout>} />
              <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
              <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
              <Route path="/services" element={<MainLayout><ServicesPage /></MainLayout>} />
              <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
              <Route path="/terms" element={<MainLayout><TermsOfServicePage /></MainLayout>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route
                path="/panel"
                element={
                  <ProtectedRoute
                    requireRoles={['panelist']}
                    requirePermissions={[PERMISSIONS.assignedInterviewsView]}
                  >
                    <MainLayout showFooter={false}>
                      <PanelLayout><PanelDashboardPage /></PanelLayout>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/panel/interviews"
                element={
                  <ProtectedRoute
                    requireRoles={['panelist']}
                    requirePermissions={[PERMISSIONS.assignedInterviewsView]}
                  >
                    <MainLayout showFooter={false}>
                      <PanelLayout><PanelWorkspacePage /></PanelLayout>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              
              {/* Protected routes - no footer for dashboard pages */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <DashboardPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/resumes"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <ResumePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <ProfilePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/applications"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <ApplicationsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/messages"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <MessagesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/videos"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <VideosPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/testimonials"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <TestimonialPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/saved-jobs"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <SavedJobsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              
              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAnyPermissions={ADMIN_WORKSPACE_PERMISSIONS}>
                    <AdminDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/team"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.teamManage]}>
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/users" element={<Navigate to="/admin/team" replace />} />
              <Route
                path="/admin/jobs"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.jobsManage]}>
                    <AdminJobsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/create"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.jobsManage]}>
                    <AdminJobCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/:id/edit"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.jobsManage]}>
                    <AdminJobCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/:jobId/applications"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.jobsManage]}>
                    <AdminJobApplicationsRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/:jobId"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.jobsManage]}>
                    <AdminJobDetailView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/applications"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.applicationsManage]}>
                    <AdminApplicationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/resumes"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.applicationsManage]}>
                    <AdminResumesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.teamManage]}>
                    <AdminCompaniesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies/:id"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.teamManage]}>
                    <AdminCompanyDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/candidates"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.applicationsManage]}>
                    <AdminCandidatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/candidates/:id"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.applicationsManage]}>
                    <AdminCandidateProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/messages"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.messagesManage]}>
                    <AdminMessagesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.applicationsManage]}>
                    <AdminAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/theme"
                element={
                  <ProtectedRoute requireRoles={['owner']}>
                    <AdminThemeSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/testimonials"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.jobsManage]}>
                    <AdminTestimonialsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requireRoles={['owner']}>
                    <AdminSystemSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/files"
                element={
                  <ProtectedRoute requirePermissions={[PERMISSIONS.teamManage]}>
                    <AdminFileManagerPage />
                  </ProtectedRoute>
                }
              />
              
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <QueryProvider>
          <AnalyticsProvider>
            <ToastProvider>
              <ThemeProvider>
                <AuthProvider>
                  <AppContent />
                  <CookieConsent />
                </AuthProvider>
              </ThemeProvider>
            </ToastProvider>
          </AnalyticsProvider>
        </QueryProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
